import { useMemo, useRef, useEffect } from 'react';
import type { Unit } from '@/types';
import { CLUSTER_CONFIG } from '../mapConfig';

/**
 * Map Clustering Configuration Hook
 *
 * Generates GeoJSON FeatureCollection from units and provides clustering configuration.
 * Two sources share the same GeoJSON: one unclustered (dots) and one clustered (zoom 9+).
 *
 * Performance Optimizations:
 * - Memoized to prevent unnecessary GeoJSON regeneration
 * - localStorage cache for fast map initialization
 * - Cache invalidated when unit count OR coordinates change (via hash comparison)
 */

// Cache configuration
const CACHE_KEY = 'map_geojson_cache_v4';
const CACHE_TIMESTAMP_KEY = 'map_geojson_cache_timestamp_v4';
const CACHE_UNIT_COUNT_KEY = 'map_geojson_unit_count_v4';
const CACHE_COORDS_HASH_KEY = 'map_geojson_coords_hash_v4';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes (balance between performance and freshness)

// Clean up old v3 cache keys on module load (one-time migration)
if (typeof window !== 'undefined') {
  try {
    localStorage.removeItem('map_geojson_cache_v3');
    localStorage.removeItem('map_geojson_cache_timestamp_v3');
    localStorage.removeItem('map_geojson_unit_count_v3');
  } catch {
    // Ignore errors during cleanup
  }
}

/**
 * Generate a simple hash from unit coordinates to detect location changes.
 * Uses a checksum approach: sum of all lat/lng values truncated to 4 decimals.
 * This catches coordinate updates even when unit count stays the same.
 */
function generateCoordsHash(units: Unit[]): string {
  // Sort by ID for consistent ordering
  const sorted = [...units].sort((a, b) => a.id.localeCompare(b.id));

  // Create a hash from id + coordinates (4 decimal precision ~11m accuracy)
  const coordString = sorted
    .map(t => `${t.id}:${t.location.lat.toFixed(4)},${t.location.lng.toFixed(4)}`)
    .join('|');

  // Simple hash function (djb2)
  let hash = 5381;
  for (let i = 0; i < coordString.length; i++) {
    hash = ((hash << 5) + hash) + coordString.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString(16);
}

interface UseMapClusteringProps {
  units: Unit[];
}

interface UseMapClusteringReturn {
  geojson: GeoJSON.FeatureCollection;
  clusterConfig: typeof CLUSTER_CONFIG.clusters;
}

/**
 * Produces a GeoJSON FeatureCollection and returns the cluster configuration.
 *
 * Generates a FeatureCollection of unit points shared by two Mapbox sources (dots and clusters).
 * Uses an optimized caching strategy with fast in-place updates when only status/alerts change,
 * and invalidates the cache when unit structure or size changes.
 *
 * @param units - Array of unit objects used to build the GeoJSON features
 * @returns An object containing:
 *   - `geojson`: the GeoJSON FeatureCollection of unit features
 *   - `clusterConfig`: cluster configuration for the clustered source
 */
// Empty GeoJSON constant - stable reference to prevent hook re-runs
const EMPTY_GEOJSON: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

export function useMapClustering({ units }: UseMapClusteringProps): UseMapClusteringReturn {
  // Track if cache was used for logging
  const cacheUsed = useRef(false);

  // Convert units to GeoJSON FeatureCollection (with smart caching)
  // Strategy: Cache structure, update properties in-place for status/alert changes
  const prevUnitsRef = useRef<Unit[]>([]);
  const cachedGeojsonRef = useRef<GeoJSON.FeatureCollection | null>(null);

  // Early return for empty units - prevents complex useMemo logic from running
  // on initial render when units array is empty (causes React Error #310)
  const hasUnits = units.length > 0;

  const geojson: GeoJSON.FeatureCollection = useMemo(() => {
    // Handle empty units case first - return stable empty GeoJSON
    if (!hasUnits) {
      return EMPTY_GEOJSON;
    }

    const prev = prevUnitsRef.current;
    const cached = cachedGeojsonRef.current;

    // Check if only status/alerts changed (locations/IDs same)
    const structureUnchanged =
      cached &&
      prev.length === units.length &&
      (() => {
        const prevMap = new Map(prev.map(t => [t.id, t]));
        return units.every(ct => {
          const pt = prevMap.get(ct.id);
          return pt && 
                 pt.location.lat === ct.location.lat && 
                 pt.location.lng === ct.location.lng;
        });
      })();

    if (structureUnchanged) {
      // Fast path: Update only status/alerts without regenerating
      console.log(`🗺️ [useMapClustering] Fast update (status/alerts only)`);

      const unitMap = new Map(units.map(t => [t.id, t]));

      return {
        ...cached!,
        features: cached!.features.map(feature => {
          if (!feature.properties) return feature;
          const unit = unitMap.get(feature.properties.id);
          if (!unit) return feature; // Should not happen

          return {
            ...feature,
            properties: {
              ...feature.properties,
              status: unit.status,
              alerts: unit.alerts || 0,
              unitData: JSON.stringify(unit),
            },
          };
        }),
      };
    }

    // Structure changed - need full regeneration
    // Try to load from cache first
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      const cachedUnitCount = localStorage.getItem(CACHE_UNIT_COUNT_KEY);
      const cachedCoordsHash = localStorage.getItem(CACHE_COORDS_HASH_KEY);

      const now = Date.now();
      const timestamp = cachedTimestamp ? parseInt(cachedTimestamp, 10) : 0;
      const age = now - timestamp;
      const previousUnitCount = cachedUnitCount ? parseInt(cachedUnitCount, 10) : 0;
      const currentCoordsHash = generateCoordsHash(units);

      // Use cache if:
      // 1. Cache exists
      // 2. Cache is not expired (< 5 minutes old)
      // 3. Unit count hasn't changed (invalidate cache if unit count changed)
      // 4. Coordinates hash matches (invalidate if any unit location changed)
      if (cachedData && age < CACHE_DURATION && previousUnitCount === units.length && cachedCoordsHash === currentCoordsHash) {
        const parsed = JSON.parse(cachedData);
        cacheUsed.current = true;
        console.log(`🗺️ [useMapClustering] Using cached GeoJSON (${units.length} units, age: ${Math.round(age / 1000)}s)`);

        // Update cached structure with current status/alerts using ID-based lookup
        const unitMap = new Map(units.map(t => [t.id, t]));

        parsed.features.forEach((feature: { properties: Record<string, unknown> }) => {
          const unit = unitMap.get(feature.properties.id as string);
          if (unit) {
            feature.properties.status = unit.status;
            feature.properties.alerts = unit.alerts || 0;
            feature.properties.unitData = JSON.stringify(unit);
          }
        });

        cachedGeojsonRef.current = parsed;
        prevUnitsRef.current = units;
        return parsed;
      }

      // Cache miss or expired
      if (cachedData) {
        const reason = age >= CACHE_DURATION
          ? 'expired'
          : previousUnitCount !== units.length
            ? `unit count changed (${previousUnitCount} → ${units.length})`
            : `coordinates changed (hash: ${cachedCoordsHash?.slice(0, 8)} → ${currentCoordsHash.slice(0, 8)})`;
        console.log(`🗺️ [useMapClustering] Cache invalidated: ${reason}`);
      }
    } catch (error) {
      console.warn('🗺️ [useMapClustering] Cache read error:', error);
    }

    // Generate fresh GeoJSON
    cacheUsed.current = false;
    console.log(`🗺️ [useMapClustering] Generating fresh GeoJSON (${units.length} units)`);

    const freshGeojson = {
      type: 'FeatureCollection' as const,
      features: units.map((unit) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [unit.location.lng, unit.location.lat],
        },
        properties: {
          id: unit.id,
          name: unit.name,
          unit_code: unit.unit_code,
          status: unit.status,
          industry_type: unit.industry_type,
          alerts: unit.alerts || 0,
          // Store full unit data as JSON string for event handlers
          unitData: JSON.stringify(unit),
        },
      })),
    };

    // Save to cache (with quota error handling)
    try {
      const coordsHash = generateCoordsHash(units);
      const cacheData = JSON.stringify(freshGeojson);

      // Clear existing entries first to free up space and avoid quota errors
      // This prevents accumulation of stale data when cache is near quota
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      localStorage.removeItem(CACHE_UNIT_COUNT_KEY);
      localStorage.removeItem(CACHE_COORDS_HASH_KEY);

      // Only cache if data isn't too large (rough 5MB localStorage limit, leave headroom)
      // GeoJSON for large fleets can be several MB stringified
      if (cacheData.length < 3 * 1024 * 1024) {
        localStorage.setItem(CACHE_KEY, cacheData);
        localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
        localStorage.setItem(CACHE_UNIT_COUNT_KEY, units.length.toString());
        localStorage.setItem(CACHE_COORDS_HASH_KEY, coordsHash);
        console.log(`🗺️ [useMapClustering] GeoJSON cached (hash: ${coordsHash.slice(0, 8)})`);
      } else {
        console.log(`🗺️ [useMapClustering] Skipping cache - data too large (${Math.round(cacheData.length / 1024)}KB)`);
      }
    } catch (error) {
      // On quota error, clear cache and continue without caching
      // This prevents repeated failed write attempts that spam the console
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('🗺️ [useMapClustering] Storage quota exceeded, clearing cache');
        try {
          localStorage.removeItem(CACHE_KEY);
          localStorage.removeItem(CACHE_TIMESTAMP_KEY);
          localStorage.removeItem(CACHE_UNIT_COUNT_KEY);
          localStorage.removeItem(CACHE_COORDS_HASH_KEY);
        } catch {
          // Ignore cleanup errors
        }
      } else {
        console.warn('🗺️ [useMapClustering] Cache write error:', error);
      }
    }

    cachedGeojsonRef.current = freshGeojson;
    prevUnitsRef.current = units;
    return freshGeojson;
  }, [units, hasUnits]);

  // Log cache usage on mount for debugging
  useEffect(() => {
    if (cacheUsed.current) {
      console.log(`✅ [useMapClustering] Map initialization used cached data`);
    }
  }, []);

  return {
    geojson,
    clusterConfig: CLUSTER_CONFIG.clusters,
  };
}